import React, { useEffect, useState } from "react";
import { Badge, Tooltip } from "@mantine/core";
import dayjs from "dayjs";

type SyncPausedBadgeProps = {
  syncPausedUntil?: string | null;
  syncPauseReason?: string | null;
};

const SyncPausedBadge: React.FC<SyncPausedBadgeProps> = ({
  syncPausedUntil,
  syncPauseReason,
}) => {
  const MAX_TIMEOUT_MS = 2_147_483_647;
  const [, setTick] = useState(0);
  const parsed = syncPausedUntil ? dayjs(syncPausedUntil) : null;
  const isActive = parsed?.isValid() && parsed.isAfter(dayjs());

  useEffect(() => {
    if (!isActive || !syncPausedUntil) return undefined;
    let timer: ReturnType<typeof setTimeout>;
    const schedule = (): void => {
      const ms = dayjs(syncPausedUntil).diff(dayjs());
      if (ms <= 0) {
        setTick((t) => t + 1);
        return;
      }
      timer = setTimeout(schedule, Math.min(ms, MAX_TIMEOUT_MS));
    };
    schedule();
    return () => { clearTimeout(timer); };
  }, [syncPausedUntil, isActive]);

  if (!isActive || !parsed) {
    return null;
  }

  const tooltipLabel = (
    <>
      Sync paused until {parsed.format("D MMM YYYY")}
      {syncPauseReason && (
        <>
          <br />
          Reason: {syncPauseReason}
        </>
      )}
    </>
  );

  return (
    <Tooltip label={tooltipLabel} wrapLines width={250}>
      <Badge
        size="sm"
        style={{ backgroundColor: "rgba(255, 165, 0, 0.15)" }}
        sx={{ color: "#b36b00", fontWeight: 500 }}
      >
        Sync Paused
      </Badge>
    </Tooltip>
  );
};

export default SyncPausedBadge;
